import type { Breakpoint, SvgIconProps } from "@mui/material";
import { Avatar, Container, Stack, Typography } from "@mui/material";
import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import type { IntegrationAppStatus } from "../../../api/types/clientTypes";
import { pageRoutes } from "../../../routes";
import ApplicationStatusTag from "../../common/ApplicationStatusTag";
import GeneralPageHeader from "../../common/GeneralPageHeader";

interface Props {
  title: string;
  logoSrc?: string;
  Icon?: FC<SvgIconProps>;
  overviewText?: string;
  appStatus: IntegrationAppStatus | undefined;
  maxWidth?: Breakpoint;
  hideBorderLine?: boolean;
  disableGutters?: boolean;
}

const IntegrationPageContainer = ({
  title,
  logoSrc,
  Icon,
  overviewText,
  appStatus,
  maxWidth,
  hideBorderLine,
  disableGutters,
  children,
}: PropsWithChildren<Props>) => {
  const { client } = useParams();

  return (
    <>
      <GeneralPageHeader
        showDefaultBackButtonTo={`/${client}/${pageRoutes.settings}/${pageRoutes.settingsIntegrations}`}
        hideBorderLine={hideBorderLine}
        TitleComponent={
          <>
            {logoSrc && <Avatar src={logoSrc} variant="square" alt="logo" sx={{ width: 32, height: 32 }} />}
            {Icon && <Icon sx={{ width: 32, height: 32 }} />}
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Typography variant="h6">{title}</Typography>
              <ApplicationStatusTag appStatus={appStatus ?? "NotConnected"} />
            </Stack>
          </>
        }
      />

      <ScrollableFlexContainer>
        <Container
          disableGutters={disableGutters}
          maxWidth={maxWidth ?? false}
          sx={{ display: "flex", flexDirection: "column", height: "100%", pt: 2.5 }}
        >
          {overviewText && (
            <Stack spacing={1} sx={{ pb: 2.5 }}>
              <Typography variant="subtitle1">Overview</Typography>
              <Typography>{overviewText}</Typography>
            </Stack>
          )}
          {children}
        </Container>
      </ScrollableFlexContainer>
    </>
  );
};

export default IntegrationPageContainer;
